import { Client, Gender } from './Client';
import { ClientFile } from './ClientFile';
import { Company } from './Company';
import { Insurance, InsuranceRecommendation, RecommendationReasons } from './Insurance';
import { InsuranceAppendix } from './InsuranceAppendix';
import { InsuranceDiscount } from './InsuranceDiscount';

export enum InsurancePolicyStatuses {
    Initial = 'initial',
    SentToClient = 'sentToClient',
    AgentFilled = 'agentFilled',
    SignedByClient = 'signedByClient',
    SignedBySpouse = 'signedBySpouse',
    SignedByAgent = 'signedByAgent',
    SentToInsuranceCompany = 'sentToInsuranceCompany',
    MissingInfo = 'missingInfo',
    Rejected = 'rejected',
    Done = 'done',
}

export enum InsurancePolicyMainBranch {
    carInsurance = 'carInsurance',
    homeInsurance = 'homeInsurance',
    healthInsurance = 'healthInsurance',
    lifeInsurance = 'lifeInsurance',
    workDisability = 'workDisability',
    longTermCare = 'longTermCare',
    criticalIllness = 'criticalIllness',
    mortgageInsurance = 'mortgageInsurance',
    personalInjury = 'personalInjury',
    dentalInsurance = 'dentalInsurance',
    touristMedicalInsurance = 'touristMedicalInsurance',
}

export enum InsurancePolicyPlanTypes {
    individual = 'individual',
    group = 'group',
    gropHMO = 'groupHMO',
}

export interface PolicyHolder {
    name: string;
    idNumber: string;
    dateOfBirth?: Date;
    gender?: Gender;
    smokerStatus?: string;
    occupation?: string;
    occupationMultiplier?: number;
}

// Data structure for a single coverage selection with pricing ID
export interface SelectedCoverage {
    coverageType: string;
    insurancePricingId: string;
}

// Data structure for per-person coverage selection during policy creation
export interface InsuredPersonCoverageSelection {
    insuredPerson: PolicyHolder;
    selectedCoverages: SelectedCoverage[]; // Array of coverage selections with pricing IDs
    smokerStatus?: 'smoker' | 'nonSmoker'; // Only relevant for life insurance and critical illness
}

export interface InsurancePolicy {
    _id: string;
    isMortgage?: boolean;
    policyFile?: ClientFile;
    annualReports?: ClientFile[];
    isSelected?: boolean;
    createdAt?: Date;
    appendices?: InsuranceAppendix[];
    client?: Client | string;
    insurance?: Insurance | string;
    clientId: string;
    status: InsurancePolicyStatuses;
    comment?: string;
    policyNumber?: string;
    totalCoverage?: number;
    company: Company | string;
    branch: InsurancePolicyMainBranch;
    subBranch?: string;
    planType: InsurancePolicyPlanTypes;
    insuranceId: string;
    isAgentAppointed?: boolean;
    isNewPolicy?: boolean;
    recommendation: InsuranceRecommendation;
    recommendationReason?: RecommendationReasons;
    totalPremium?: number;
    premiumDiscountPercentage?: number;
    premiumDiscountCode?: string;
    premiumTerm?: 'monthly' | 'yearly';
    issueDate?: Date;
    insuranceStartDate?: Date;
    insuranceEndDate?: Date;
    insuredList?: PolicyHolder[];
    cancellationAppendixA?: boolean;
    cancellationAppendixC?: boolean;
    noCancellationRequired?: boolean;
    replacementPolicies?: InsurancePolicy[];
    templates: {
        generalTemplates: string[];
        quastionnaires: string[];
    };
    file?: ClientFile;
    commissions?: {
        depositPercentage: number;
        monthlyPercentage: number;
        depositAmount: number;
        monthlyAmount: number;
    };
    submissionDate?: Date;
    submissionValidUntil?: Date;
    productionDate?: Date;
    cancellationDate?: Date;
    missingDataFirstDate?: Date;
    isCollateral?: boolean;
    collectionIssue?: {
        hasIssue: boolean;
        date: Date;
        remarks: string;
    };
    isRejected?: boolean;
    rejectionReason?: string;
    clientSignatureDate?: Date;
    spouseSignatureDate?: Date;
    agentSignatureDate?: Date;
    agentFilledDate?: Date;
    lastChangeDate?: Date;
    selectedCoverages?: string[];
    insuranceDiscount?: string | InsuranceDiscount | undefined;
    insuredPersonCoverages?: InsuredPersonCoverageSelection[]; // For creating appendices per person
    remarks?: string; // Free text remarks/notes about the policy
    mortgageDetails?: {
        amount: number;
        period: number;
        interestRate: number;
    }[];
}

/**
 * if main branch is healthInsurance, then the branch of the associated appendix can be either health,
 * criticalIllness, cancedrIllness or personalAccidents.
 * if main branch is lifeInsurance, then the branch of the associated appendix can be either risk, mortageRisk, workDisability or insuranceUmbrella.
 * if the main branch is something else then those two, then the branch of the associated appendix is empty.
 * now for the appendix branchSection field. if appendix branch is health, then the branchSection can be either drugs or surgeries.
 * if the appendix branch is risk, then the branchSection can be either accidentDisability or death.
 * if the appendix branch is mortgageRisk, then the branchSection can be only death.
 * if the appendix branch is workDisability, then the branchSection can be either cancelation or roof.
 * if the appendix branch is insuranceUmbrella, then the branchSection can be either specialProfession or franchise.
 * if the appendix branch is personalAccidents, then the branchSection can be only professionalDissability.
 * if the appendix branch is something else then those, then the branchSection is empty.
 */
